import { Component, type SyntheticEvent } from "react";
import { observer } from "mobx-react";
import {
    Stack,
    DefaultButton,
    TextField,
    Separator,
    type IDropdownOption,
    Dropdown,
    IconButton,
    ActionButton,
} from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import {
    type PreOpsChecklist,
    type PreOpsChecklistCreate,
    type PreOpsQuestionCreate,
    PreOpsQuestionKind,
    PreOpsQuestionSortKey,
    SortDirection,
} from "../../api";
import { type FormCreateProps } from "../../utils/form-create-props";
import { doubleBindString } from "../../utils/double-bind";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { defaultPageSize } from "../../utils/constants";
import { ChecklistLogoutTimer } from "../atoms/checklist-logout-timer";

export interface FormCreatePreOpsChecklistProps extends FormCreateProps<PreOpsChecklist> {}

import { store } from "../../store";

@observer
export class FormCreatePreOpsChecklist extends Component<FormCreatePreOpsChecklistProps> {
    @observable public accessor label = "";
    // `null` means the checklist has no logout timer, which is the default.
    @observable public accessor minutesUntilLogout: number | null = null;
    // Contains a list of validationIds and create structs for new questions.
    // Each question has their own validation state.
    @observable public accessor questions: [UUID, PreOpsQuestionCreate][] = [];

    private validationId = createUuid();

    constructor(props: FormCreatePreOpsChecklistProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryPreOpsChecklists.validation.create.updateModel(
            this.validationId,
            this.preOpsChecklistCreate,
        );
    }

    private triggerQuestionCreateValidation(
        validationId: UUID,
        create: PreOpsQuestionCreate,
    ): void {
        store.repositoryPreOpsQuestions.validation.create.updateModel(validationId, create);
    }

    componentDidMount(): void {
        store.repositoryPreOpsChecklists.validation.create.initializeModel(
            this.validationId,
            this.preOpsChecklistCreate,
        );

        this.addQuestion();
    }

    @computed private get preOpsChecklistCreate(): PreOpsChecklistCreate {
        const { label, minutesUntilLogout } = this;

        return {
            label,
            minutesUntilLogout,
        };
    }

    // Compile the JSX for the question list of the form.
    private renderQuestions(): JSX.Element[] {
        const responseOptions: IDropdownOption[] = [
            {
                key: PreOpsQuestionKind.GOOD_OK_CRITICAL,
                text: store.i18n.t("formUpdatePreOpsChecklist.responseOptions.goodOkCritical"),
            },
            {
                key: PreOpsQuestionKind.YES_NO,
                text: store.i18n.t("formUpdatePreOpsChecklist.responseOptions.yesNo"),
            },
        ];

        return this.questions.map((question, index) => {
            // Only add labels to the very first question.
            let questionLabel = undefined;
            let responseOptionsLabel = undefined;
            // Only show the "required" marker for the first question
            let required = false;
            let canDeleteFirst = true;
            if (index === 0) {
                questionLabel = store.i18n.t("formCreatePreOpsChecklist.questions.label");
                responseOptionsLabel = store.i18n.t(
                    "formCreatePreOpsChecklist.responseOptions.label",
                );
                required = true;

                // Don't make the first question deletable, if it's the only one.
                if (this.questions.length === 1) {
                    canDeleteFirst = false;
                }
            }
            return (
                <Stack.Item key={index}>
                    <Stack
                        horizontal
                        tokens={{
                            childrenGap: "2rem",
                        }}
                    >
                        <Stack.Item
                            styles={{
                                root: {
                                    minWidth: "500px",
                                },
                            }}
                        >
                            <TextField
                                autoAdjustHeight
                                multiline
                                // FluentUI uses a <textarea /> for the multiline <TextField /> component.
                                // Style the textarea to look like a single line input:
                                styles={{
                                    field: {
                                        marginTop: 2,
                                        height: 28,
                                        minHeight: 28,
                                        padding: "0 8px",
                                        lineHeight: 26,
                                    },
                                    fieldGroup: {
                                        minHeight: 32,
                                    },
                                }}
                                prefix={`${index + 1}.`}
                                required={required}
                                value={question[1].body}
                                label={questionLabel}
                                onChange={(event) =>
                                    this.changeQuestion(index, event.currentTarget.value)
                                }
                                errorMessage={store.i18n.formatFieldValidationState(
                                    store.repositoryPreOpsQuestions.validation.create.getFieldValidationState(
                                        question[0],
                                        "body",
                                    ),
                                )}
                            />
                        </Stack.Item>
                        <Stack.Item grow>
                            <Dropdown
                                placeholder={store.i18n.t(
                                    "formCreatePreOpsChecklist.responseOptions.placeholder",
                                )}
                                label={responseOptionsLabel}
                                options={responseOptions}
                                selectedKey={question[1].kind}
                                onChange={(_event, option) =>
                                    this.changeQuestionKind(
                                        index,
                                        option?.key as PreOpsQuestionKind,
                                    )
                                }
                            />
                        </Stack.Item>
                        <Stack.Item align="end" style={{ marginLeft: "1rem" }}>
                            {canDeleteFirst && (
                                <IconButton
                                    iconProps={{ iconName: "Trash" }}
                                    onClick={() => this.deleteQuestion(index)}
                                />
                            )}
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
            );
        });
    }

    @action.bound private changeMinutesUntilLogout(minutesUntilLogout: number | null): void {
        this.minutesUntilLogout = minutesUntilLogout;
        this.triggerValidation();
    }

    @action.bound private changeQuestion(index: number, text: string): void {
        this.questions[index][1].body = text;

        // Trigger creation validation for this question
        this.triggerQuestionCreateValidation(this.questions[index][0], this.questions[index][1]);
    }

    @action.bound private changeQuestionKind(index: number, kind: PreOpsQuestionKind): void {
        this.questions[index][1].kind = kind;

        // Trigger creation validation for this question
        this.triggerQuestionCreateValidation(this.questions[index][0], this.questions[index][1]);
    }

    @action.bound private deleteQuestion(index: number): void {
        this.questions.splice(index, 1);
    }

    @action.bound private addQuestion(): void {
        const validationId = createUuid();
        const create: PreOpsQuestionCreate = {
            kind: PreOpsQuestionKind.GOOD_OK_CRITICAL,
            body: "",
        };

        store.repositoryPreOpsQuestions.validation.create.initializeModel(validationId, create);
        this.questions.push([validationId, create]);
    }

    @action.bound
    private async createPreOpsChecklist(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const preOpsChecklist = await store.repositoryPreOpsChecklists.create(
            this as PreOpsChecklistCreate,
        );
        for (const question of this.questions) {
            await store.repositoryPreOpsQuestions.create({
                body: question[1].body,
                kind: question[1].kind,
                checklistId: preOpsChecklist.id,
            } as PreOpsQuestionCreate);
        }

        // Reload the query for all questions once they're created.
        // The checklist is already displayed and reloaded in the background while
        // we're creating the individual questions, but not all questions exist at that point yet.
        await store.repositoryPreOpsQuestions.reloadQuery({
            pageSize: defaultPageSize,
            sortKey: PreOpsQuestionSortKey.POSITION,
            sortDirection: SortDirection.ASCENDING,
            checklistId: preOpsChecklist.id,
        });
        await store.repositoryPreOpsChecklists.reloadQuery({ pageSize: defaultPageSize });
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(preOpsChecklist);
        }
    }

    @action.bound private reset(): void {
        this.label = "";
        this.minutesUntilLogout = null;
        this.questions = [];
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreatePreOpsChecklist.submit.text")}
                validation={store.repositoryPreOpsChecklists.validation.create}
                validationId={this.validationId}
            />
        );
        const questions = this.renderQuestions();
        return (
            <form onSubmit={this.createPreOpsChecklist}>
                <Stack
                    tokens={{
                        childrenGap: "1em",
                        padding: `0px ${sizes.formPaddingHorizontal}`,
                    }}
                >
                    <Stack styles={{ root: { maxWidth: "500px" } }}>
                        <TextField
                            label={store.i18n.t("formCreatePreOpsChecklist.label.label")}
                            {...doubleBindString<FormCreatePreOpsChecklist>(this, "label", () =>
                                this.triggerValidation(),
                            )}
                            required
                            errorMessage={store.i18n.formatFieldValidationState(
                                store.repositoryPreOpsChecklists.validation.create.getFieldValidationState(
                                    this.validationId,
                                    "label",
                                ),
                            )}
                        />
                    </Stack>

                    <ChecklistLogoutTimer
                        minutesUntilLogout={this.minutesUntilLogout}
                        onChange={this.changeMinutesUntilLogout}
                        translationPrefixKey="formCreatePreOpsChecklist"
                    />
                </Stack>
                <Separator />
                <Stack
                    tokens={{
                        childrenGap: "1em",
                        padding: `0px ${sizes.formPaddingHorizontal}`,
                    }}
                    styles={{
                        root: {
                            maxHeight: "50vh",
                            overflow: "auto",
                        },
                    }}
                >
                    {questions}
                    <Stack.Item key="add">
                        <ActionButton
                            iconProps={{ iconName: "Add" }}
                            text={store.i18n.t("formCreatePreOpsChecklist.addQuestion.text")}
                            onClick={this.addQuestion}
                        />
                    </Stack.Item>
                </Stack>

                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreatePreOpsChecklist.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
